import type { GlobusCaptionTrack, GlobusJobContent, GlobusJobsOverviewContent } from './globusJobContent.js';

export type LocalizedGlobusJobContent = Omit<Partial<GlobusJobContent>, 'cta' | 'captionTracks'> & {
  cta?: Partial<GlobusJobContent['cta']>;
  captionTracks?: Array<Partial<GlobusCaptionTrack>>;
};

const trGlobusCaptionTracks: Array<Partial<GlobusCaptionTrack>> = [
  { label: 'Almanca' },
  { label: 'İngilizce' },
  { label: 'Türkçe' },
];

const enGlobusCaptionTracks: Array<Partial<GlobusCaptionTrack>> = [
  { label: 'German' },
  { label: 'English' },
  { label: 'Turkish' },
];

export const trGlobusJobs: LocalizedGlobusJobContent[] = [
  {
    eyebrow: 'İş ilanı',
    title: 'Fırıncı',
    introduction: 'Taze unlu mamullerin kokusunu seviyor ve geleneksel fırıncılığı modern sistemlerle birleştirmek mi istiyorsun?',
    description:
      'GLOBUS Usta Fırınında, denenmiş ve bölgesel tariflerle her gün taze unlu mamuller hazırlanır. Gerçek malzemeler, modern üretim sistemleri ve iyi fırıncılığa olan tutkuyu paylaşan bir ekiple çalışırsın.',
    tasksHeading: 'Görevlerin',
    tasks: [
      'Malzemeleri hassas biçimde tartmak, hamurları usulüne uygun hazırlamak ve yoğurmak',
      'Denenmiş tariflerle ekmek, küçük ekmek ve diğer unlu mamulleri üretmek',
      'Pişirme süreçlerini hazırlamak, takip etmek ve kontrol etmek',
      'Modern makineleri ve üretim sistemlerini kullanmak ve bakımlarını yapmak',
      'Kalite, güvenlik ve hijyen kurallarına uymak',
    ],
    requirementsHeading: 'Senden beklediklerimiz',
    requirements: [
      'Tercihen fırıncılık eğitimi veya mesleki deneyim',
      'El emeğiyle hazırlanan unlu mamullere ilgi',
      'Fiziksel dayanıklılık ve işe aktif biçimde katılan bir çalışma tarzı',
      'Görev alanına göre erken saatte çalışmaya hazır olma ve günlük iş ile güvenlik kuralları için yeterli Almanca bilgisi',
    ],
    benefitsHeading: 'Seni neler bekliyor',
    benefits: ['Tam zamanlı pozisyonlar mümkün olabilir', 'Uzun vadeli ve süresiz çalışma imkânları', 'Genellikle pazar ve resmî tatil günlerinde çalışma yoktur'],
    videoTitle: 'GLOBUS fırıncı iş ilanı videosu',
    captionTracks: trGlobusCaptionTracks,
    cta: { label: 'Şimdi fırıncı olarak başvur' },
  },
  {
    eyebrow: 'İş ilanı',
    title: 'Kasap',
    introduction: 'Titiz çalışıyor ve geleneksel gıda zanaatını modern üretim yöntemleriyle birleştirmek mi istiyorsun?',
    description:
      'GLOBUS Usta Kasabında et ve şarküteri ürünlerini gerçek zanaatkârlıkla üretirsin. Geleneksel yöntemler, denenmiş tarifler ve modern makinelerle çalışırsın.',
    tasksHeading: 'Görevlerin',
    tasks: [
      'Eti usulüne uygun parçalamak, kesmek ve sonraki işlemler için hazırlamak',
      'Mağazaya özel şarküteri ürünleri, jambon ve bölgesel lezzetler üretmek',
      'Kuter, tütsü fırını, pişirme kazanı ve dolum makineleri gibi üretim makinelerini kullanmak',
      'Kaliteye, temizliğe ve gıdaların sorumlu biçimde işlenmesine dikkat etmek',
      'İş güvenliği ve hijyen kurallarına eksiksiz uymak',
    ],
    requirementsHeading: 'Senden beklediklerimiz',
    requirements: [
      'Tercihen kasaplık eğitimi veya bu alanda mesleki deneyim',
      'Geleneksel gıda zanaatından keyif alma',
      'Fiziksel dayanıklılık ve işe aktif biçimde katılan bir çalışma tarzı',
      'İşletmedeki vardiyalara uyum sağlayabilme ve günlük iş ile güvenlik kuralları için yeterli Almanca bilgisi',
    ],
    benefitsHeading: 'Seni neler bekliyor',
    benefits: ['Tam veya yarı zamanlı pozisyonlar mevcut olabilir', 'Uzun vadeli ve süresiz çalışma imkânları', 'Mesleki işe alıştırma ve gelişim olanakları'],
    videoTitle: 'GLOBUS kasap iş ilanı videosu',
    captionTracks: trGlobusCaptionTracks,
    cta: { label: 'Şimdi kasap olarak başvur' },
    warning: {
      title: 'Görevle ilgili önemli bilgi',
      text: 'GLOBUS Usta Kasabında domuz eti de işlenmektedir. Bu nedenle günlük iş hayatında domuz eti ve domuz etinden üretilen ürünlerle çalışmaya hazır olmalısın.',
    },
  },
  {
    eyebrow: 'İş ilanı',
    title: 'Taze ürün tezgâhı çalışanı',
    introduction: 'Kaliteli gıdalardan hoşlanıyor ve müşterilere güler yüzlü, yetkin bir hizmet sunmak mı istiyorsun?',
    description:
      'Taze ürün tezgâhında kişisel danışmanlığı kaliteli gıdalarla birleştirirsin. Ürünlerin taze, iştah açıcı ve düzenli sunulmasını, müşterilerin de güler yüzlü ve yetkin biçimde bilgilendirilmesini sağlarsın.',
    distinction:
      'Kasaplıkta ağırlık üretim ve işlemededir. Taze ürün tezgâhında ise danışmanlık, satış, sunum ve kalite kontrol ön plandadır. Çalışacağın tezgâh alanı işe başlamadan önce açıkça kararlaştırılır.',
    tasksHeading: 'Görevlerin',
    tasks: [
      'Müşterilere güler yüzle danışmanlık yapmak ve ilgili taze ürün tezgâhında hizmet vermek',
      'Ürünleri uzmanlıkla açıklamak ve satmak',
      'Tezgâhı çekici biçimde düzenlemek, tamamlamak ve temiz tutmak',
      'Düzenli tazelik ve kalite kontrolleri yapmak',
      'Hijyen kurallarına güvenilir biçimde uymak',
    ],
    requirementsHeading: 'Senden beklediklerimiz',
    requirements: [
      'İnsanlarla iletişimden ve kaliteli, taze gıdalardan keyif alma',
      'Düzen, temizlik ve ürün sunumu konusunda iyi bir bakış',
      'Güvenilirlik ve takım içinde çalışmaktan keyif alma',
      'Pazartesiden cumartesiye çalışma saatlerine uyum sağlayabilme ve danışmanlık ile günlük iş için yeterli Almanca bilgisi',
    ],
    benefitsHeading: 'Seni neler bekliyor',
    benefits: ['Tam veya yarı zamanlı pozisyonlar mevcut olabilir', 'Pozisyona göre farklı bir meslekten geçiş mümkün olabilir', 'Planlı işe alıştırma ve mesleki gelişim'],
    videoTitle: 'GLOBUS taze ürün tezgâhı iş ilanı videosu',
    captionTracks: trGlobusCaptionTracks,
    cta: { label: 'Şimdi taze ürün tezgâhı için başvur' },
    warning: {
      title: 'Görevle ilgili önemli bilgi',
      text: 'Et tezgâhındaki bir görevde domuz eti de işlenmektedir. Domuz etiyle çalışmaya hazır olmalısın.',
    },
  },
  {
    eyebrow: 'İş ilanı',
    title: 'Gastronomi çalışanı',
    introduction: 'Taze malzemelerle yemek yapmayı seviyor ve restoran yoğun olduğunda da güler yüzlü kalıyor musun?',
    description:
      'GLOBUS gastronomisinde sıcak ve soğuk yemekleri taze hazırlar, iştah açıcı biçimde sunar ve misafirlere güler yüzle servis edersin. Mağazaya göre Asia-Wok, tavuk ızgarası, pizza fırını veya başka bir gastronomi alanında çalışabilirsin.',
    tasksHeading: 'Görevlerin',
    tasks: [
      'Sıcak ve soğuk yemekleri taze hazırlamak',
      'Yemekleri iştah açıcı biçimde sunmak ve misafirlere servis etmek',
      'Misafirlere güler yüzle danışmanlık yapmak ve hizmet vermek',
      'Gerektiğinde kasa sistemini kullanmak',
      'İş, kalite ve hijyen kurallarına uymak',
    ],
    requirementsHeading: 'Senden beklediklerimiz',
    requirements: [
      'Yemek yapmaktan ve taze malzemelerle çalışmaktan keyif alma',
      'Motivasyon, güvenilirlik ve misafirlerle iletişimde güler yüz',
      'Pazartesiden cumartesiye değişen vardiyalara uyum sağlayabilme',
      'Günlük iş için yeterli Almanca bilgisi; uygun pozisyonlarda farklı bir meslekten geçiş mümkün olabilir',
    ],
    benefitsHeading: 'Seni neler bekliyor',
    benefits: ['Tam veya yarı zamanlı çalışma modelleri mevcut olabilir', 'Uzun vadeli ve süresiz çalışma imkânları', 'Genellikle mağaza kapanışından sonra iş olmaz'],
    videoTitle: 'GLOBUS gastronomi iş ilanı videosu',
    captionTracks: trGlobusCaptionTracks,
    cta: { label: 'Şimdi gastronomi için başvur' },
  },
];

export const enGlobusJobs: LocalizedGlobusJobContent[] = [
  {
    eyebrow: 'Job opening',
    title: 'Baker',
    introduction: 'Do you love the smell of fresh baked goods and want to combine traditional baking with modern equipment?',
    description:
      'The GLOBUS master bakery makes fresh baked goods every day using proven regional recipes. You work with genuine ingredients, modern production equipment and a team that shares your passion for excellent baking.',
    tasksHeading: 'Your tasks',
    tasks: [
      'Weigh ingredients precisely, prepare doughs correctly and knead them',
      'Make bread, rolls and other baked goods using proven recipes',
      'Prepare, monitor and control the baking process',
      'Operate and maintain modern machinery and production equipment',
      'Follow quality, safety and hygiene requirements',
    ],
    requirementsHeading: 'What you should bring',
    requirements: [
      'Ideally, vocational training or professional experience in baking',
      'Enthusiasm for traditionally crafted baked goods',
      'Physical resilience and a hands-on approach to work',
      'Readiness to start early depending on the role, plus sufficient German for everyday work and safety requirements',
    ],
    benefitsHeading: 'What you can expect',
    benefits: ['Full-time positions may be available', 'Long-term and permanent employment opportunities', 'Generally no work on Sundays or public holidays'],
    videoTitle: 'Video for the baker job opening at GLOBUS',
    captionTracks: enGlobusCaptionTracks,
    cta: { label: 'Apply as a baker now' },
  },
  {
    eyebrow: 'Job opening',
    title: 'Butcher',
    introduction: 'Do you work precisely and want to combine traditional food craftsmanship with modern production methods?',
    description:
      'In the GLOBUS master butchery, you produce meat and sausage products using genuine craftsmanship. You work with traditional methods, proven recipes and modern machinery.',
    tasksHeading: 'Your tasks',
    tasks: [
      'Professionally debone and cut meat, then prepare it for further processing',
      'Produce in-house sausages, ham and regional specialities',
      'Operate production equipment such as cutters, smoke ovens, sausage kettles and filling machines',
      'Maintain quality and cleanliness and handle food responsibly',
      'Consistently follow workplace safety and hygiene requirements',
    ],
    requirementsHeading: 'What you should bring',
    requirements: [
      'Ideally, vocational training or professional experience as a butcher or meat processor',
      'Enthusiasm for traditional food craftsmanship',
      'Physical resilience and a hands-on approach to work',
      'Flexibility within operational shifts, plus sufficient German for everyday work and safety requirements',
    ],
    benefitsHeading: 'What you can expect',
    benefits: ['Full-time and part-time positions may be available', 'Long-term and permanent employment opportunities', 'Professional onboarding and training opportunities'],
    videoTitle: 'Video for the butcher job opening at GLOBUS',
    captionTracks: enGlobusCaptionTracks,
    cta: { label: 'Apply as a butcher now' },
    warning: {
      title: 'Important information about this role',
      text: 'The GLOBUS master butchery also processes pork. You should therefore be prepared to work with pork and products made from pork in your day-to-day role.',
    },
  },
  {
    eyebrow: 'Job opening',
    title: 'Fresh-food counter team member',
    introduction: 'Do you enjoy high-quality food and want to offer customers friendly, knowledgeable advice?',
    description:
      'At the fresh-food counter, you combine personal advice with high-quality food. You ensure products are presented fresh, appetising and well organised while offering customers friendly, knowledgeable service.',
    distinction:
      'Butchery focuses primarily on production and processing. At the fresh-food counter, the focus is on advice, sales, presentation and quality control. Your specific counter area is agreed transparently before you start.',
    tasksHeading: 'Your tasks',
    tasks: [
      'Welcome, advise and serve customers at the relevant fresh-food counter',
      'Explain and sell products with confidence and expertise',
      'Arrange, replenish and keep the counter clean',
      'Carry out regular freshness and quality checks',
      'Follow hygiene requirements reliably',
    ],
    requirementsHeading: 'What you should bring',
    requirements: [
      'Enjoyment of meeting people and an interest in high-quality, fresh food',
      'A good eye for order, cleanliness and product presentation',
      'Reliability and enjoyment of teamwork',
      'Flexibility during opening hours from Monday to Saturday, plus sufficient German for customer advice and everyday work',
    ],
    benefitsHeading: 'What you can expect',
    benefits: ['Full-time and part-time positions may be available', 'Career changers may be welcome depending on the specific role', 'Structured onboarding and professional training'],
    videoTitle: 'Video for the fresh-food counter job opening at GLOBUS',
    captionTracks: enGlobusCaptionTracks,
    cta: { label: 'Apply for the fresh-food counter now' },
    warning: {
      title: 'Important information about this role',
      text: 'An assignment at the meat counter also involves processing pork. You should be prepared to work with pork.',
    },
  },
  {
    eyebrow: 'Job opening',
    title: 'Hospitality team member',
    introduction: 'Do you enjoy cooking with fresh ingredients and stay friendly when the restaurant gets busy?',
    description:
      'In GLOBUS hospitality, you freshly prepare hot and cold dishes, present them attractively and serve guests with a friendly manner. Depending on the store, you might work at the Asian wok, chicken grill, pizza oven or in another food-service area.',
    tasksHeading: 'Your tasks',
    tasks: [
      'Freshly prepare hot and cold dishes',
      'Present dishes attractively and serve them to guests',
      'Advise and serve guests in a friendly manner',
      'Operate the till system when needed',
      'Follow workplace, quality and hygiene requirements',
    ],
    requirementsHeading: 'What you should bring',
    requirements: [
      'Enjoyment of cooking and working with fresh ingredients',
      'Motivation, reliability and a friendly manner with guests',
      'Flexibility for changing shifts from Monday to Saturday',
      'Sufficient German for everyday work; career changers may be considered for suitable roles',
    ],
    benefitsHeading: 'What you can expect',
    benefits: ['Full-time and part-time models may be available', 'Long-term and permanent employment opportunities', 'Your working day generally ends no later than store closing time'],
    videoTitle: 'Video for the hospitality job opening at GLOBUS',
    captionTracks: enGlobusCaptionTracks,
    cta: { label: 'Apply for hospitality now' },
  },
];

export const trGlobusJobsOverview: Partial<GlobusJobsOverviewContent> = {
  eyebrow: 'GLOBUS’taki güncel işler',
  title: 'GLOBUS’ta iş ilanları',
  intro: 'Bir GLOBUS mağazasındaki zanaat ve hizmet odaklı alanların görevlerini, koşullarını ve fırsatlarını keşfet.',
  videoUnavailable: 'Video şu anda kullanılamıyor. Pozisyonla ilgili tüm bilgileri hemen yanında bulabilirsin.',
  videoLoading: 'Video yükleniyor',
  sharedBenefitsEyebrow: 'Ortak avantajlar',
  sharedBenefitsTitle: 'GLOBUS’taki uzun vadeli başlangıcın için güçlü imkânlar.',
  sharedBenefitsText: 'Birçok GLOBUS pozisyonu güvenilir kariyer olanaklarını ek işveren avantajlarıyla birleştirir. Hangi imkânların senin pozisyonun için geçerli olduğunu seninle açıkça netleştiririz.',
  sharedBenefits: [
    'Çalışma modeline bağlı olarak altı haftaya kadar izin',
    'Çalışan indirimi ile olası izin ve Noel ikramiyeleri',
    'Pozisyona göre işveren destekli emeklilik ve tasarruf katkıları',
    'Eğitim ve gelişim olanakları',
    'Dakika bazında çalışma süresi kaydı',
    'Göreve göre pazar ve resmî tatil günlerinde izin',
  ],
  conditionsTitle: 'Çalışma koşulları hakkında bilgi',
  conditionsText: 'Somut çalışma koşulları ve avantajlar konuma ve pozisyona göre değişebilir. Çalışma saatleri, ücret, görev yeri ve bağlayıcı avantajlar başvurudan önce açıkça görüşülür.',
};

export const enGlobusJobsOverview: Partial<GlobusJobsOverviewContent> = {
  eyebrow: 'Current jobs at GLOBUS',
  title: 'Job openings at GLOBUS',
  intro: 'Explore the tasks, requirements and opportunities in the skilled and service-focused areas of a GLOBUS store.',
  videoUnavailable: 'The video is currently unavailable. You can find all information about the role right beside it.',
  videoLoading: 'Video is loading',
  sharedBenefitsEyebrow: 'Shared benefits',
  sharedBenefitsTitle: 'Strong benefits for your long-term start at GLOBUS.',
  sharedBenefitsText: 'Many GLOBUS roles combine reliable prospects with additional employment benefits. We explain transparently which of them apply to your specific position.',
  sharedBenefits: [
    'Up to six weeks of leave, depending on the working-time model',
    'Employee discount and possible holiday and Christmas bonuses',
    'Occupational pension and employer-supported savings benefits depending on the role',
    'Training and development opportunities',
    'Working time recorded to the minute',
    'Sundays and public holidays off depending on the role',
  ],
  conditionsTitle: 'Information about employment conditions',
  conditionsText: 'Specific employment conditions and benefits may vary by location and position. Working hours, pay, place of work and binding benefits are discussed transparently before an application.',
};
